import type { ComponentType } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  parsePath,
  type Path,
  type RouterNavigateOptions,
  type To,
} from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { Fragment } from "react";

// Eager: the app shell. These render on (nearly) every route, so splitting them
// out would only add a round trip before anything can paint.
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import TicketsRedirect from "@/pages/TicketsRedirect";
import CustomerChatConfigRedirect from "@/pages/CustomerChatConfigRedirect";
import RouteFallback from "@/components/RouteFallback";
import OidcProtectedLayout from "@/layouts/OidcProtectedLayout";
import SidebarLayout from "@/layouts/SidebarLayout";
import SettingsLayout, {
  type SettingsRouteHandle,
} from "@/layouts/SettingsLayout";
import SmartRedirect from "@/components/SmartRedirect";
import NotFoundPage from "@/pages/NotFoundPage";
import NotEnabledPage from "@/pages/NotEnabledPage";
import PausedRouteGuard from "@/components/PausedRouteGuard";
import { TENANT_PARAM } from "@/providers/ImpersonationProvider";

/**
 * Page components are loaded per route rather than imported up front.
 *
 * Statically importing all of them put every page's dependency tree in the
 * entry chunk — sigma/graphology (only /ontology), recharts (only
 * /service-kpis), dagre (only /ontology/pipeline), tiptap/prosemirror,
 * react-table, dnd-kit — so the login screen paid for the whole app. React
 * Router resolves `lazy` before committing a navigation, so the previous page
 * stays on screen while a chunk downloads; the cold-start case is covered by
 * the root route's HydrateFallback.
 */
function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

/** Same as `lazyPage`, for a page exported under a name instead of default. */
function lazyNamedPage<K extends string>(
  load: () => Promise<Record<K, ComponentType>>,
  exportName: K,
) {
  return async () => ({ Component: (await load())[exportName] });
}

function NuqsLayout() {
  return (
    <NuqsAdapter>
      <Outlet />
    </NuqsAdapter>
  );
}

type Router = ReturnType<typeof createBrowserRouter>;

/**
 * Keep the staff user's tenant (`?tenant=<slug>`, #733) on every URL the app makes:
 * link hrefs and navigations carry the current location's tenant over unless they
 * name one. Search-only targets (`setSearchParams`, nuqs) already hold the full query
 * they want, so they pass unchanged. Non-staff URLs have no tenant: a no-op for them.
 */
function keepTenantParam(router: Router): Router {
  const withTenant = <T extends Partial<Path>>(path: T): T => {
    const tenant = new URLSearchParams(router.state.location.search).get(TENANT_PARAM);
    const search = new URLSearchParams(path.search);
    if (!tenant || !path.pathname || search.has(TENANT_PARAM)) return path;
    search.set(TENANT_PARAM, tenant);
    return { ...path, search: `?${search}` };
  };
  const navigate = router.navigate.bind(router);
  const createHref = router.createHref.bind(router);
  router.navigate = (to: number | To | null, opts?: RouterNavigateOptions) =>
    typeof to === "number"
      ? navigate(to)
      : navigate(
          to === null ? to : withTenant(typeof to === "string" ? parsePath(to) : to),
          opts,
        );
  router.createHref = (location) =>
    createHref(location instanceof URL ? location : withTenant(location));
  return router;
}

export default function createRouter() {
  return keepTenantParam(createBrowserRouter(
    [
      {
        id: "root",
        path: "/",
        Component: NuqsLayout,
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouteFallback,
        children: [
          {
            Component: OidcProtectedLayout,
            children: [
              {
                index: true,
                Component: SmartRedirect,
              },
              {
                path: "auth/:action",
                Component: Fragment,
              },
              {
                path: "not-enabled",
                Component: NotEnabledPage,
              },
              {
                // Unlinked: the browser plugin testers' ticket picker.
                path: "neptune-test/:conversationId?",
                lazy: lazyPage(() => import("@/pages/NeptuneTestPage")),
              },
              {
                Component: SidebarLayout,
                children: [
                  {
                    path: "tenant-overview",
                    lazy: lazyPage(() => import("@/pages/TenantOverviewPage")),
                  },
                  {
                    path: "administration",
                    lazy: lazyPage(() => import("@/pages/AdministrationPage")),
                  },
                  {
                    path: "contacts",
                    lazy: lazyPage(() => import("@/pages/ContactContextsPage")),
                  },
                  {
                    path: "contacts/:contactContextId",
                    lazy: lazyPage(() => import("@/pages/ContactDetailPage")),
                  },
                  {
                    path: "contacts/company/:companyContextId",
                    lazy: lazyPage(() => import("@/pages/CompanyDetailPage")),
                  },
                  {
                    // Moved under Ontology; keep the old path as a redirect.
                    path: "style-examples",
                    element: <Navigate to="/ontology/prescriptive" replace />,
                  },
                  {
                    path: "team",
                    lazy: lazyPage(() => import("@/pages/TeamPage")),
                  },
                  {
                    path: "calls",
                    lazy: lazyPage(() => import("@/pages/CallsPage")),
                  },
                  {
                    path: "service-chatbot",
                    element: <Navigate to="/agent/general" replace />,
                  },
                  {
                    path: "ontology",
                    lazy: lazyPage(() => import("@/pages/OntologyPage")),
                  },
                  {
                    path: "ontology/entity/:name",
                    lazy: lazyPage(() => import("@/pages/OntologyEntityPage")),
                  },
                  {
                    path: "ontology/docs",
                    lazy: lazyPage(() => import("@/pages/OntologyDocsPage")),
                  },
                  {
                    path: "ontology/prescriptive",
                    lazy: lazyPage(
                      () => import("@/pages/PrescriptiveKnowledgePage"),
                    ),
                  },
                  {
                    // Customer-facing alias for the same page: tenant admins
                    // reach it as "Antwort-Steuerung" (control over how the AI
                    // answers), not through the technical /ontology path.
                    path: "antwort-steuerung",
                    lazy: lazyPage(
                      () => import("@/pages/PrescriptiveKnowledgePage"),
                    ),
                  },
                  {
                    path: "ontology/pipeline",
                    lazy: lazyPage(
                      () => import("@/pages/OntologyPipelinePage"),
                    ),
                  },
                  {
                    path: "ontology/machine-hierarchy",
                    lazy: lazyPage(
                      () => import("@/pages/MachineHierarchyPage"),
                    ),
                  },
                  {
                    // Customer-facing alias (tenant admins reach it from the
                    // sidebar as "Maschinenpark"); same page as the staff URL.
                    path: "maschinenpark",
                    lazy: lazyPage(
                      () => import("@/pages/MachineHierarchyPage"),
                    ),
                  },
                  {
                    path: "knowledge-check",
                    lazy: lazyPage(() => import("@/pages/KnowledgeCheckPage")),
                  },
                  {
                    path: "retriever-test",
                    lazy: lazyPage(() => import("@/pages/RetrieverTestPage")),
                  },
                  // Dashboard temporarily disabled due to heavy queries
                  // {
                  //   path: "dashboard",
                  //   lazy: lazyPage(() => import("@/pages/DashboardPage")),
                  // },
                  {
                    path: "documents",
                    lazy: lazyPage(() => import("@/pages/DocumentsPage")),
                  },
                  {
                    path: "emails/:emailId?",
                    element: <Navigate to="/agent/portal" replace />,
                  },
                  {
                    path: "knowledge_base",
                    lazy: lazyPage(() => import("@/pages/KnowledgeBasePage")),
                  },
                  {
                    path: "products",
                    lazy: lazyPage(() => import("@/pages/ProductsPage")),
                  },
                  {
                    path: "products/:productId",
                    lazy: lazyPage(() => import("@/pages/ProductDetailPage")),
                  },
                  {
                    path: "replacement_parts",
                    lazy: lazyPage(
                      () => import("@/pages/ReplacementPartsPage"),
                    ),
                  },
                  {
                    path: "usage",
                    lazy: lazyPage(() => import("@/pages/UsagePage")),
                  },
                  {
                    path: "service-kpis",
                    lazy: lazyPage(() => import("@/pages/SuccessKpisPage")),
                  },
                  {
                    path: "settings",
                    Component: SettingsLayout,
                    children: [
                      {
                        index: true,
                        lazy: lazyPage(
                          () => import("@/pages/settings/SettingsPage"),
                        ),
                      },
                      {
                        path: "intents",
                        lazy: lazyPage(
                          () => import("@/pages/settings/IntentsPage"),
                        ),
                      },
                      {
                        path: "cg",
                        lazy: lazyPage(
                          () =>
                            import("@/pages/settings/conversation-interfaces"),
                        ),
                      },
                      {
                        path: "cg/:connectorId",
                        lazy: lazyPage(
                          () =>
                            import("@/pages/settings/conversation-interfaces"),
                        ),
                      },
                      {
                        path: "ms-exchange",
                        lazy: lazyPage(
                          () =>
                            import("@/pages/settings/MicrosoftExchangePage"),
                        ),
                      },
                      {
                        path: "data-clients",
                        lazy: lazyPage(
                          () => import("@/pages/settings/DataClientsPage"),
                        ),
                      },
                      {
                        path: "org",
                        lazy: lazyPage(
                          () => import("@/pages/settings/OrgSettingsPage"),
                        ),
                      },
                      {
                        path: "sla",
                        // The page centres itself at its own width (form and simulation).
                        handle: {
                          fullWidth: true,
                        } satisfies SettingsRouteHandle,
                        lazy: lazyPage(
                          () => import("@/pages/settings/sla/SlaSettingsPage"),
                        ),
                      },
                      {
                        path: "customer-chat-config",
                        Component: CustomerChatConfigRedirect,
                      },
                      {
                        path: "account",
                        lazy: lazyPage(
                          () => import("@/pages/settings/AccountPage"),
                        ),
                      },
                    ],
                  },
                  {
                    path: "eval",
                    lazy: lazyPage(() => import("@/pages/eval/EvalListPage")),
                  },
                  // Dataset and conversation live in the path so a curator can
                  // paste a link at exactly what they are looking at. Declared
                  // before "eval/:evalRunId" so "curate" is not read as a run id.
                  {
                    path: "eval/curate",
                    lazy: lazyPage(() => import("@/pages/eval/EvalCuratePage")),
                  },
                  {
                    path: "eval/curate/:datasetId",
                    lazy: lazyPage(() => import("@/pages/eval/EvalCuratePage")),
                  },
                  {
                    path: "eval/curate/:datasetId/:conversationId",
                    lazy: lazyPage(() => import("@/pages/eval/EvalCuratePage")),
                  },
                  {
                    path: "eval/label",
                    lazy: lazyPage(() => import("@/pages/eval/EvalLabelPage")),
                  },
                  {
                    path: "eval/label/:labellingRunId",
                    lazy: lazyPage(() => import("@/pages/eval/EvalLabelPage")),
                  },
                  {
                    path: "eval/scores/:scoreName",
                    lazy: lazyPage(() => import("@/pages/eval/EvalScorePage")),
                  },
                  {
                    path: "eval/:evalRunId",
                    lazy: lazyPage(
                      () => import("@/pages/eval/EvalRunDetailPage"),
                    ),
                  },
                  {
                    path: "reranker",
                    lazy: lazyPage(
                      () => import("@/pages/reranker/RerankerPage"),
                    ),
                  },
                  {
                    path: "changelog",
                    lazy: lazyPage(
                      () => import("@/pages/changelog/ChangelogPage"),
                    ),
                  },
                  {
                    path: "db-tables",
                    lazy: lazyPage(
                      () => import("@/pages/settings/SqlTablesPage"),
                    ),
                  },
                  {
                    path: "db-tables/:tableId",
                    lazy: lazyPage(
                      () => import("@/pages/settings/SqlTableDetailPage"),
                    ),
                  },
                  {
                    path: "board",
                    lazy: lazyPage(() => import("@/pages/TicketBoardPage")),
                  },
                  {
                    path: "tickets",
                    element: <Navigate to="/agent/portal" replace />,
                  },
                  { path: "tickets/:ticketId", Component: TicketsRedirect },
                  {
                    path: "archive",
                    lazy: lazyPage(() => import("@/pages/ArchivePage")),
                  },
                  {
                    path: "archive/:conversationId",
                    lazy: lazyPage(() => import("@/pages/ArchivePage")),
                  },
                  {
                    path: "archive/message/:messageId",
                    lazy: lazyPage(() => import("@/pages/ArchivePage")),
                  },
                  // The AI surfaces. A paused trial (tenant.offboarded-reason
                  // "testphase") loses exactly these and keeps every other page.
                  {
                    Component: PausedRouteGuard,
                    children: [
                      {
                        path: "agent",
                        element: <Navigate to="/agent/general" replace />,
                      },
                      {
                        path: "agent/general",
                        lazy: lazyNamedPage(
                          () => import("@/pages/AgentPage"),
                          "AgentGeneralPage",
                        ),
                      },
                      {
                        path: "agent/general/:chatId",
                        lazy: lazyNamedPage(
                          () => import("@/pages/AgentPage"),
                          "AgentGeneralPage",
                        ),
                      },
                      {
                        path: "agent/customer-chat",
                        lazy: lazyPage(() => import("@/pages/CustomerChatPage")),
                      },
                      {
                        path: "agent/customer-chats/:conversationId?",
                        lazy: lazyPage(() => import("@/pages/CustomerChatsPage")),
                      },
                      {
                        path: "agent/portal",
                        lazy: lazyNamedPage(
                          () => import("@/pages/AgentPage"),
                          "AgentPortalPage",
                        ),
                      },
                      {
                        path: "agent/:conversationId",
                        lazy: lazyPage(() => import("@/pages/AgentPage")),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "*",
            Component: NotFoundPage,
          },
        ],
      },
    ],
    { basename: "/" },
  ));
}
